/*!
 * © 2024. The EGT Universe. All Rights Reserved.
 * License: Requires ACTIVE AppArtisan Subscription.
 * For more information visit: https://egtuniverse.com/legal
 *
 * Created for Universe App Tools.
 *
 * Created by Justin K Kazmierczak.
 */
var _ = require("../../../uam/module.js")(exports, {
  namespace: "ua.wizard",
  title: "Wizard",
  control: true,
  description: "A step-by-step wizard for controlling a user guided process.",
  styling: {
    styles: [
      {
        selector: ".ua-wizard-progress",
        styles: [
          "height: 2rem;"
        ]
      }
    ]
  },
  fields: {
    "inner": {
      title: "Inner Content",
      type: "array",
      "of": "inner",
      description: "The steps of the wizard, each with a title, description, and optional icon.",
      required: true
    },
    "name": {
      title: "Name",
      type: "string",
      description: "The submit URL of the wizard.",
      required: true
    },
    "progressbarclass": {
      title: "Progress Bar Class",
      type: "string",
      description: "The class of the progress bar.",
      default: "bg-primary"
    },
    "continueText": {
      title: "Continue Button Text",
      type: "string",
      description: "The text to display on the continue button.",
      default: ""
    },
    "successTitle": {
      title: "Success Title",
      type: "string",
      description: "Optional title shown on the final success confirmation screen.",
      default: ""
    },
    "successDescription": {
      title: "Success Description",
      type: "string",
      description: "Optional description shown on the final success confirmation screen.",
      default: ""
    },
    "successIcon": {
      title: "Success Icon",
      type: "string",
      description: "Bootstrap icon class suffix for the success confirmation screen.",
      default: "check2-circle"
    },
    "successDocumentFields": {
      title: "Success Document Fields",
      type: "string",
      description: "Comma-separated document field names to show in success details. Empty shows all non-sensitive fields.",
      default: ""
    },
    "successResponseFields": {
      title: "Success Response Fields",
      type: "string",
      description: "Comma-separated response field names to show in success details. Empty shows all non-sensitive fields.",
      default: ""
    },
    "successHideToolbar": {
      title: "Success Hide Toolbar",
      type: "boolean",
      description: "If true, hides the wizard footer after successful submission.",
      default: true
    },
    "successShowDocument": {
      title: "Success Show Document",
      type: "boolean",
      description: "If true, shows submitted document values on the success confirmation screen.",
      default: true
    },
    "successShowResponse": {
      title: "Success Show Response",
      type: "boolean",
      description: "If true, shows response values from the route on the success confirmation screen.",
      default: true
    },
    "phi": {
      title: "Protected Health Information",
      type: "boolean",
      description: "If true, drafts are not stored in localStorage. Requires a draft-url for server-side draft saving.",
      default: false
    },
    "draft-url": {
      title: "Draft URL",
      type: "string",
      description: "Server endpoint for saving and loading PHI drafts.",
      default: ""
    },
    "errors": {
      title: "Error Overrides",
      type: "array",
      "of": "inner",
      description: "Wizard-specific error message overrides keyed by error namespace.",
      default: []
    }
  }
});

var handlerz = [];
var uai = require("../interface/interface.js");
var events = require("../../../uam/events.js");

// ─── Global error registry ────────────────────────────────────────────────────
// Priority order (higher wins):
// 0 = original error payload
// 1 = dashboard override
// 2 = plugin override
// 3 = app override
var _errorRegistry = {};
var ERROR_PRIORITY = {
  original: 0,
  dashboard: 1,
  plugin: 2,
  app: 3
};

function _registerError(namespace, def, priority) {
  var ns = (namespace || "").toString().trim();
  if (!ns || !def) return;
  var p = typeof priority === "number" ? priority : ERROR_PRIORITY.dashboard;
  // Higher number wins (more specific overrides more general)
  if (!_errorRegistry[ns] || p >= _errorRegistry[ns]._p) {
    _errorRegistry[ns] = {
      _p: p,
      namespace: ns,
      title: (def.title || "").toString(),
      description: (def.description || "").toString(),
      actions: def.actions || null,
      close: def.close !== false
    };
  }
}

function _sanitizePathString(text) {
  var origin = "";
  try {
    origin = window.location.origin || "";
  } catch (e) { /* ignore */ }
  var value = (text || "").toString();
  value = value.replace(/(\/(?:Users|home|var|opt|srv|tmp|private|Volumes|workspace|workspaces|app|usr\/src|root)\/[^\s)'"`]+)+/g, "@!/");
  if (origin) {
    value = value.split(origin).join("@!");
  }
  return value;
}

function _sanitizeStack(stack) {
  if (!stack) return stack;
  if (typeof stack === "string") return _sanitizePathString(stack);
  if (stack instanceof Array) {
    return stack.map(function(item) {
      if (typeof item === "string") return _sanitizePathString(item);
      if (item && typeof item === "object") {
        var next = { ...item };
        if ("file" in next) next.file = _sanitizePathString(next.file);
        if ("s" in next) next.s = _sanitizePathString(next.s);
        return next;
      }
      return item;
    });
  }
  return stack;
}

function _normalizeErrorObject(serverError) {
  if (!serverError) {
    return {
      namespace: "",
      title: "Validation Error",
      description: "Please review the highlighted fields and try again.",
      _priority: ERROR_PRIORITY.original
    };
  }

  if (typeof serverError === "string") {
    return {
      namespace: "",
      title: "Validation Error",
      description: serverError,
      _priority: ERROR_PRIORITY.original
    };
  }

  var err = (serverError.error && typeof serverError.error === "object") ? serverError.error : serverError;
  var title = err.title || err.name || "Validation Error";
  var description = err.description || err.message || "Please review the highlighted fields and try again.";

  return {
    ...err,
    namespace: (err.namespace || "").toString().trim(),
    title: title,
    description: description,
    stack: _sanitizeStack(err.stack),
    stacktrace: _sanitizeStack(err.stacktrace || err.stack),
    _priority: ERROR_PRIORITY.original
  };
}

function _resolveError(wizard, serverError) {
  var baseError = _normalizeErrorObject(serverError);
  var ns = baseError.namespace;

  var dashboardOverride = null;
  if (ns) {
    try {
      var wizardErrors = JSON.parse(wizard.getAttribute("data-wizard-errors") || "{}");
      if (wizardErrors[ns]) {
        dashboardOverride = {
          ...wizardErrors[ns],
          namespace: ns,
          _priority: ERROR_PRIORITY.dashboard
        };
      }
    } catch (e) { /* ignore */ }
  }

  var registryOverride = null;
  if (ns && _errorRegistry[ns]) {
    registryOverride = _errorRegistry[ns];
  }

  var best = baseError;
  if (dashboardOverride && dashboardOverride._priority >= best._priority) {
    best = { ...best, ...dashboardOverride };
  }
  if (registryOverride && registryOverride._p >= best._priority) {
    best = { ...best, ...registryOverride, _priority: registryOverride._p };
  }

  return best;
}

// ─── Draft helpers ────────────────────────────────────────────────────────────

function _draftKey(wizard) {
  var uaeEl = wizard.closest("uae");
  return "ua-wiz:" + ((uaeEl && uaeEl.getAttribute("name")) || wizard.getAttribute("id") || "");
}

function _isPhiWizard(wizard) {
  return wizard.getAttribute("data-wizard-phi") === "true";
}

function _isDashboardContext(wizard) {
  return !!wizard.closest("[data-ua-init-type]");
}

function _loadDraft(wizard) {
  try {
    var raw = localStorage.getItem(_draftKey(wizard));
    if (!raw) return null;
    var json = decodeURIComponent(escape(atob(raw)));
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

function _saveDraft(wizard, data) {
  if (_isPhiWizard(wizard)) {
    _saveServerDraft(wizard, data);
    return;
  }
  try {
    var json = JSON.stringify(data);
    var encoded = btoa(unescape(encodeURIComponent(json)));
    localStorage.setItem(_draftKey(wizard), encoded);
  } catch (e) { /* ignore */ }
}

function _clearDraft(wizard) {
  if (_isPhiWizard(wizard)) {
    var draftUrl = (wizard.getAttribute("data-wizard-draft-url") || "").trim();
    if (draftUrl) {
      fetch(draftUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "clear" })
      }).catch(function() {});
    }
    return;
  }
  try {
    localStorage.removeItem(_draftKey(wizard));
  } catch (e) { /* ignore */ }
}

function _saveServerDraft(wizard, data) {
  var draftUrl = (wizard.getAttribute("data-wizard-draft-url") || "").trim();
  if (!draftUrl) return;
  fetch(draftUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "save", data: data })
  }).catch(function() {});
}

async function _loadServerDraft(wizard) {
  var draftUrl = (wizard.getAttribute("data-wizard-draft-url") || "").trim();
  if (!draftUrl) return null;
  try {
    var response = await fetch(draftUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "get" })
    });
    if (!response.ok) return null;
    var result = await response.json();
    if (result && result.success && result.data) return result.data;
  } catch (e) { /* ignore */ }
  return null;
}

async function _captureDraft(wizard) {
  var doc = await _collectDocument(wizard);
  _saveDraft(wizard, doc.flat);
}

function _attachDraftListeners(wizard) {
  wizard.addEventListener("change", function() {
    _captureDraft(wizard); // intentionally not awaited — fire-and-forget
  });
  wizard.addEventListener("focusout", function(e) {
    var t = e.target;
    if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT")) {
      _captureDraft(wizard); // intentionally not awaited — fire-and-forget
    }
  });
}

// ─── Document collection (all steps, via UATools.save per-step) ───────────────

/**
 * Collects the full document being built by the wizard across all steps.
 * Uses window.UATools.save() per step (no validation) so complex controls
 * serialise correctly. Falls back to _collectFields() when UATools is not
 * available (e.g. during server-side render).
 * @param {HTMLElement} wizard The [data-wizard='true'] element.
 * @returns {Promise<{flat: object, steps: Array}>}
 */
async function _collectDocument(wizard) {
  var wizardId = wizard.getAttribute("id");
  var totalSteps = Number(wizard.getAttribute("data-wizard-steps"));
  var flat = {};
  var steps = [];

  for (var i = 1; i <= totalSteps; i++) {
    var stepSelector = '[data-wizard-id="' + wizardId + '"][data-wizard-step="' + i + '"]';
    var stepData = {};

    if (window.UATools && typeof window.UATools.save === "function") {
      try {
        var stepResult = await window.UATools.save(stepSelector, false);
        for (var key in stepResult) {
          if (key === "success") continue;
          var ctrl = stepResult[key];
          if (ctrl && ctrl.data !== null && ctrl.data !== undefined) {
            stepData[key] = ctrl.data;
          }
        }
      } catch (e) {
        // fallback for this step
        var stepEl = document.querySelector(stepSelector);
        if (stepEl) stepData = _collectFields(stepEl);
      }
    } else {
      // UATools not available — use raw field scrape for this step
      var stepEl = document.querySelector(stepSelector);
      if (stepEl) stepData = _collectFields(stepEl);
    }

    steps.push({ step: i, data: stepData });
    Object.assign(flat, stepData);
  }

  return { flat: flat, steps: steps };
}

// ─── Field collection (fallback / draft capture) ──────────────────────────────

function _collectFields(container) {
  var data = {};
  var inputs = container.querySelectorAll("input, select, textarea");

  // Helper: resolve field name from own attribute or nearest named ancestor
  function _resolveName(input) {
    var n = input.getAttribute("name");
    if (!n) {
      var p = input.closest("[name]");
      if (p) n = p.getAttribute("name");
    }
    return n || null;
  }

  // Pre-count how many inputs resolve to each name (detects multi-value groups)
  var nameCounts = {};
  for (var p = 0; p < inputs.length; p++) {
    var fn = _resolveName(inputs[p]);
    if (fn) nameCounts[fn] = (nameCounts[fn] || 0) + 1;
  }

  var multiValues = {};  // collected values for names that appear more than once

  for (var i = 0; i < inputs.length; i++) {
    var input = inputs[i];
    var fieldName = _resolveName(input);
    if (!fieldName) continue;

    var inputType = (input.type || "").toLowerCase();
    var isMulti = nameCounts[fieldName] > 1;

    if (inputType === "radio") {
      if (input.checked) data[fieldName] = input.value;
      continue;
    }

    if (inputType === "checkbox") {
      if (isMulti) {
        // Multiple checkboxes with same name → array of selected values
        if (!multiValues[fieldName]) multiValues[fieldName] = [];
        if (input.checked) {
          multiValues[fieldName].push(input.value === "on" ? true : input.value);
        }
      } else {
        // Single checkbox → plain boolean
        data[fieldName] = input.checked;
      }
      continue;
    }

    // Text, hidden, select, textarea, etc.
    if (isMulti) {
      // Multiple inputs with same name (e.g. ua.tags hidden inputs) → array
      if (!multiValues[fieldName]) multiValues[fieldName] = [];
      if (input.value) multiValues[fieldName].push(input.value);
    } else {
      data[fieldName] = input.value;
    }
  }

  // Merge multi-value groups into data
  for (var mName in multiValues) {
    data[mName] = multiValues[mName];
  }

  return data;
}

// ─── Validation ───────────────────────────────────────────────────────────────

async function _validateStep(wizard, stepNum) {
  var wizardId = wizard.getAttribute("id");
  var stepSelector = '[data-wizard-id="' + wizardId + '"][data-wizard-step="' + stepNum + '"]';
  var stepEl = document.querySelector(stepSelector);
  if (!stepEl) return { success: true };

  // Try UATools.save for full UA control validation (triggers per-element validation)
  if (window.UATools && typeof window.UATools.save === "function") {
    try {
      var result = await window.UATools.save(stepSelector, true);
      if (result && typeof result.success === "boolean") return result;
    } catch (e) { /* fall through to manual */ }
  }

  // Manual fallback: check [required] form inputs
  var required = stepEl.querySelectorAll("input[required], select[required], textarea[required]");
  var invalid = false;
  var manualDoc = { success: true };
  for (var r = 0; r < required.length; r++) {
    var field = required[r];
    var isCheckbox = (field.type || "").toLowerCase() === "checkbox";
    var isEmpty = isCheckbox ? !field.checked : !(field.value || "").toString().trim();
    if (isEmpty) {
      field.classList.add("is-invalid");
      field.classList.remove("is-valid");
      invalid = true;
      // Try to find a human-readable label for the error message
      var nameEl = field.closest("[name]");
      var label = (nameEl && nameEl.getAttribute("name")) || field.getAttribute("id") || "field";
      if (!manualDoc[label]) {
        manualDoc[label] = { success: false, errors: [], namespace: "ua.wizard.validation" };
      }
      manualDoc[label].errors.push({
        type: "validation",
        message: label + " is required."
      });
    } else {
      field.classList.remove("is-invalid");
      field.classList.add("is-valid");
    }
  }
  if (invalid) {
    manualDoc.success = false;
    return manualDoc;
  }
  return { success: true };
}

function _escapeHtml(str) {
  return (str || "").toString()
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function _toTitleCaseLabel(key) {
  var txt = (key || "").toString().replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  if (!txt) return "";
  return txt.charAt(0).toUpperCase() + txt.slice(1);
}

function _isSensitiveField(key) {
  var txt = (key || "").toString().toLowerCase();
  return /password|secret|token|apikey|api_key|privatekey|private_key|passkey|challenge|credential|signature/.test(txt);
}

function _formatSummaryValue(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value;
  if (value instanceof Array) {
    var mapped = value.map(function(item) {
      if (item === null || item === undefined) return "";
      if (typeof item === "object") {
        try {
          return JSON.stringify(item);
        } catch (error) {
          return String(item);
        }
      }
      return String(item);
    }).filter(function(item) { return item !== ""; });
    return mapped.join(", ");
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch (error) {
      return String(value);
    }
  }
  return String(value);
}

function _parseCsvFields(csv) {
  var txt = (csv || "").toString().trim();
  if (!txt) return [];
  return txt.split(",").map(function(item) {
    return item.toString().trim();
  }).filter(function(item) {
    return !!item;
  });
}

function _pickFields(data, allowList) {
  if (!(data && typeof data === "object")) return {};
  var selected = {};
  if (allowList instanceof Array && allowList.length > 0) {
    for (var i = 0; i < allowList.length; i++) {
      var key = allowList[i];
      if (_isSensitiveField(key)) continue;
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        selected[key] = data[key];
      }
    }
    return selected;
  }

  for (var k in data) {
    if (!Object.prototype.hasOwnProperty.call(data, k)) continue;
    if (_isSensitiveField(k)) continue;
    if (k === "confirmation" || k === "redirect" || k === "message" || k === "success") continue;
    selected[k] = data[k];
  }
  return selected;
}

function _toSummaryRows(data) {
  var rows = [];
  if (!(data && typeof data === "object")) return rows;
  for (var key in data) {
    if (!Object.prototype.hasOwnProperty.call(data, key)) continue;
    var formatted = _formatSummaryValue(data[key]);
    if (!formatted) continue;
    rows.push({
      label: _toTitleCaseLabel(key),
      value: formatted
    });
  }
  return rows;
}

function _normalizeSuccessfulPayload(result) {
  var payload = result || {};
  if (
    payload &&
    payload.success === true &&
    Object.prototype.hasOwnProperty.call(payload, "data") &&
    payload.data &&
    typeof payload.data === "object"
  ) {
    payload = payload.data;
  }
  return payload || {};
}

function _setWizardNavigationState(wizard, disabled) {
  if (!wizard) return;
  var wizardId = wizard.getAttribute("id");
  var continueBtn = document.getElementById(wizardId + "-continue");
  var backBtn = document.getElementById(wizardId + "-back");
  if (continueBtn) continueBtn.disabled = !!disabled;
  if (backBtn) backBtn.disabled = !!disabled;
}

function _setWizardCompleteState(wizard, completed, hideToolbar) {
  if (!wizard) return;
  wizard.setAttribute("data-wizard-complete", completed ? "true" : "false");
  _setWizardNavigationState(wizard, completed);
  if (completed && hideToolbar) {
    var footer = wizard.querySelector(".wizard-footer");
    if (footer) footer.classList.add("d-none");
  }
}

function _buildSuccessConfirmationConfig(wizard, docFlat, payload) {
  var routeConfirmation = (payload && payload.confirmation && typeof payload.confirmation === "object")
    ? payload.confirmation
    : {};

  var successTitle = routeConfirmation.title
    || wizard.getAttribute("data-wizard-success-title")
    || "Success";
  var successDescription = routeConfirmation.description
    || payload.message
    || wizard.getAttribute("data-wizard-success-description")
    || "Your submission was completed successfully.";
  var successIcon = routeConfirmation.icon
    || wizard.getAttribute("data-wizard-success-icon")
    || "check2-circle";

  var hideToolbar = wizard.getAttribute("data-wizard-success-hide-toolbar") !== "false";
  if (typeof routeConfirmation.hideToolbar === "boolean") {
    hideToolbar = routeConfirmation.hideToolbar;
  }

  var showDocument = wizard.getAttribute("data-wizard-success-show-document") !== "false";
  if (typeof routeConfirmation.showDocument === "boolean") {
    showDocument = routeConfirmation.showDocument;
  }

  var showResponse = wizard.getAttribute("data-wizard-success-show-response") !== "false";
  if (typeof routeConfirmation.showResponse === "boolean") {
    showResponse = routeConfirmation.showResponse;
  }

  var documentFieldList = _parseCsvFields(routeConfirmation.documentFields || wizard.getAttribute("data-wizard-success-document-fields") || "");
  var responseFieldList = _parseCsvFields(routeConfirmation.responseFields || wizard.getAttribute("data-wizard-success-response-fields") || "");

  var documentRows = [];
  var pickedDocument = _pickFields(docFlat || {}, documentFieldList);
  documentRows = _toSummaryRows(pickedDocument);

  var responseRows = [];
  var pickedResponse = _pickFields(payload || {}, responseFieldList);
  responseRows = _toSummaryRows(pickedResponse);

  var routeDetailsRows = [];
  if (routeConfirmation.details instanceof Array) {
    for (var i = 0; i < routeConfirmation.details.length; i++) {
      var detail = routeConfirmation.details[i] || {};
      var detailLabel = (detail.label || detail.title || "").toString().trim();
      var detailValue = _formatSummaryValue(detail.value);
      if (!detailLabel || !detailValue) continue;
      routeDetailsRows.push({
        label: detailLabel,
        value: detailValue
      });
    }
  }

  return {
    title: successTitle,
    description: successDescription,
    icon: successIcon,
    hideToolbar: hideToolbar,
    showDocument: showDocument,
    showResponse: showResponse,
    routeDetailsRows: routeDetailsRows,
    documentRows: documentRows,
    responseRows: responseRows
  };
}

function _buildSummarySectionHtml(title, rows) {
  if (!(rows instanceof Array) || rows.length === 0) return "";
  var items = [];
  for (var i = 0; i < rows.length; i++) {
    var row = rows[i] || {};
    var label = _escapeHtml(row.label || "");
    var value = _escapeHtml(row.value || "");
    if (!label || !value) continue;
    items.push(
      "<div class='ua-wizard-success-item'>"
      + "<div class='ua-wizard-success-item-label'>" + label + "</div>"
      + "<div class='ua-wizard-success-item-value'>" + value + "</div>"
      + "</div>"
    );
  }
  if (items.length === 0) return "";
  return (
    "<section class='ua-wizard-success-section'>"
    + "<h4 class='ua-wizard-success-section-title'>" + _escapeHtml(title) + "</h4>"
    + "<div class='ua-wizard-success-grid'>" + items.join("") + "</div>"
    + "</section>"
  );
}

function _renderSuccessConfirmation(wizard, docFlat, payload) {
  var cfg = _buildSuccessConfirmationConfig(wizard, docFlat, payload || {});
  var existing = wizard.querySelector(".ua-wizard-result");
  if (existing) existing.remove();

  var body = wizard.querySelector(".wizard");
  if (!body) return cfg;
  body.classList.add("ua-wizard-complete");
  body.innerHTML =
    "<div class='ua-wizard-success-wrap'>"
    + "<div class='ua-wizard-success-hero'>"
    + "<div class='ua-wizard-success-icon'><i class='bi bi-" + _escapeHtml(cfg.icon) + "'></i></div>"
    + "<h3 class='ua-wizard-success-title'>" + _escapeHtml(cfg.title) + "</h3>"
    + "<p class='ua-wizard-success-description'>" + _escapeHtml(cfg.description) + "</p>"
    + "</div>"
    + _buildSummarySectionHtml("Route Details", cfg.routeDetailsRows)
    + (cfg.showDocument ? _buildSummarySectionHtml("Submitted Information", cfg.documentRows) : "")
    + (cfg.showResponse ? _buildSummarySectionHtml("Response Information", cfg.responseRows) : "")
    + "</div>";

  return cfg;
}

async function _showStepErrors(wizard, stepNum, doc) {
  var stepEl = wizard.querySelector('[data-wizard-step="' + stepNum + '"]');
  if (!stepEl) return;
  var existing = stepEl.querySelector(".ua-wizard-step-errors");
  if (existing) existing.remove();

  if (!doc || doc.success) return;

  var el = document.createElement("div");
  el.className = "alert alert-danger ua-wizard-step-errors";

  try {
    if (window.UATools && typeof window.UATools.render === "function") {
      var rendered = await window.UATools.render({
        n: "ua.element.validation.error",
        title: "We found a few things to fix.",
        doc: doc
      });
      if (rendered && rendered.nodeType) {
        el.appendChild(rendered);
        stepEl.insertBefore(el, stepEl.firstChild);
        return;
      }
    }
  } catch (error) {
    console.error("Wizard validation render error", error);
  }

  var items = [];
  for (var key in doc) {
    if (key === "success" || !doc[key] || doc[key].success) continue;
    var errs = doc[key].errors;
    if (!(errs instanceof Array)) errs = errs ? [errs] : [];
    for (var i = 0; i < errs.length; i++) {
      var e = errs[i] || {};
      items.push("<li>" + _escapeHtml(e.message || e.description || "Required field missing.") + "</li>");
    }
  }
  el.innerHTML = "<ul class='mb-0 ps-3'>" + items.join("") + "</ul>";
  stepEl.insertBefore(el, stepEl.firstChild);
}

function _clearStepErrors(wizard, stepNum) {
  var stepEl = wizard.querySelector('[data-wizard-step="' + stepNum + '"]');
  if (stepEl) {
    var existing = stepEl.querySelector(".ua-wizard-step-errors");
    if (existing) existing.remove();
  }
}

function _shakeStep(wizard, stepNum) {
  var stepEl = wizard.querySelector('[data-wizard-step="' + stepNum + '"]');
  if (!stepEl) return;
  stepEl.classList.remove("ua-wizard-shake");
  void stepEl.offsetWidth; // force reflow to restart animation
  stepEl.classList.add("ua-wizard-shake");
  setTimeout(function() {
    stepEl.classList.remove("ua-wizard-shake");
  }, 600);
}

// ─── URL prefill ──────────────────────────────────────────────────────────────

function _prefillFromUrl(wizardId) {
  try {
    var params = new URLSearchParams(window.location.search);
    var data = {};
    params.forEach(function(value, key) {
      data[key] = value;
    });
    if (Object.keys(data).length > 0 && window.UATools && typeof window.UATools.load === "function") {
      window.UATools.load(data, "#" + wizardId);
    }
  } catch (e) { /* ignore */ }
}

// ─── CSS injection ────────────────────────────────────────────────────────────

var _cssInjected = false;
function _injectCSS() {
  if (_cssInjected) return;
  _cssInjected = true;
  try {
    var style = document.createElement("style");
    style.textContent = [
      "@keyframes ua-wizard-shake {",
      "  0%, 100% { transform: translateX(0); }",
      "  20% { transform: translateX(-8px); }",
      "  40% { transform: translateX(8px); }",
      "  60% { transform: translateX(-6px); }",
      "  80% { transform: translateX(6px); }",
      "}",
      ".ua-wizard-shake {",
      "  animation: ua-wizard-shake 0.5s ease-in-out;",
      "}",
      ".ua-wizard-success-wrap {",
      "  display: grid;",
      "  gap: 1rem;",
      "  padding: 0.75rem;",
      "}",
      ".ua-wizard-success-hero {",
      "  display: flex;",
      "  flex-direction: column;",
      "  align-items: center;",
      "  justify-content: center;",
      "  text-align: center;",
      "  padding: 1.5rem 1rem;",
      "  border-radius: 1rem;",
      "  background: linear-gradient(155deg, rgba(25,135,84,0.12), rgba(13,110,253,0.08));",
      "}",
      ".ua-wizard-success-icon {",
      "  width: 4rem;",
      "  height: 4rem;",
      "  border-radius: 999rem;",
      "  display: inline-flex;",
      "  align-items: center;",
      "  justify-content: center;",
      "  background: rgba(25,135,84,0.2);",
      "  color: #146c43;",
      "  font-size: 2rem;",
      "}",
      ".ua-wizard-success-title {",
      "  margin: 0.9rem 0 0.45rem 0;",
      "  font-size: 1.5rem;",
      "}",
      ".ua-wizard-success-description {",
      "  margin: 0;",
      "  max-width: 50rem;",
      "}",
      ".ua-wizard-success-section {",
      "  border: 1px solid rgba(0,0,0,0.08);",
      "  border-radius: 0.9rem;",
      "  padding: 1rem;",
      "  background: rgba(255,255,255,0.62);",
      "}",
      ".ua-wizard-success-section-title {",
      "  margin: 0 0 0.75rem 0;",
      "  font-size: 1rem;",
      "}",
      ".ua-wizard-success-grid {",
      "  display: grid;",
      "  gap: 0.65rem;",
      "  grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));",
      "}",
      ".ua-wizard-success-item {",
      "  border-radius: 0.6rem;",
      "  background: rgba(255,255,255,0.84);",
      "  border: 1px solid rgba(0,0,0,0.08);",
      "  padding: 0.7rem 0.8rem;",
      "}",
      ".ua-wizard-success-item-label {",
      "  font-size: 0.8rem;",
      "  font-weight: 700;",
      "  opacity: 0.72;",
      "}",
      ".ua-wizard-success-item-value {",
      "  margin-top: 0.2rem;",
      "  white-space: pre-wrap;",
      "  word-break: break-word;",
      "}",
      "@media (max-width: 48rem) {",
      "  .ua-wizard-success-hero { padding: 1.1rem 0.8rem; }",
      "  .ua-wizard-success-title { font-size: 1.25rem; }",
      "  .ua-wizard-success-grid { grid-template-columns: 1fr; }",
      "}"
    ].join("\n");
    document.head.appendChild(style);
  } catch (e) { /* ignore */ }
}

// ─── Render ───────────────────────────────────────────────────────────────────

exports.render = function(options) {
  var wizardId = uai.generateRandomID(12);

  if (!(Array.isArray(options.inner))) {
    options.inner = [options.inner];
  }

  var steps = options.inner.map(function(step, index) {
    var cClass = "wizard-step";
    if (index !== 0) cClass += " d-none";
    return {
      "n": "div",
      "data-wizard-id": wizardId,
      "data-wizard-step": index + 1,
      "c": cClass,
      "i": step
    };
  });

  // Build data-wizard-errors attribute from errors field
  var wizardErrorsAttr = "";
  if (options.errors && options.errors.length > 0) {
    try {
      var errMap = {};
      for (var ei = 0; ei < options.errors.length; ei++) {
        var e = options.errors[ei];
        if (e && e.namespace) errMap[e.namespace] = e;
      }
      wizardErrorsAttr = JSON.stringify(errMap);
    } catch (e2) { /* ignore */ }
  }

  // Default continue button content
  var isLastStep = options.inner.length === 1;
  var continueInner = isLastStep
    ? [{ "n": "i", "c": "bi bi-check2" }, { "n": "span", "c": "d-none d-lg-inline", "i": " Submit" }]
    : [{ "n": "i", "c": "bi bi-arrow-right" }, { "n": "span", "c": "d-none d-lg-inline", "i": " Continue" }];

  if (options.continueText && options.continueText.length > 0) {
    continueInner = [
      { "n": "span", "c": "d-lg-inline", "i": options.continueText + "\u00a0" },
      { "n": "i", "c": "bi bi-arrow-right" }
    ];
  }

  var wiz = {
    "n": "div",
    "id": wizardId,
    "data-wizard": "true",
    "name": options.name,
    "data-wizard-steps": options.inner.length,
    "data-wizard-current": "1",
    "data-wizard-hasSeen": "[1]",
    "data-wizard-phi": options.phi ? "true" : "",
    "data-wizard-draft-url": options["draft-url"] || "",
    "data-wizard-errors": wizardErrorsAttr,
    "data-wizard-success-title": options.successTitle || "",
    "data-wizard-success-description": options.successDescription || "",
    "data-wizard-success-icon": options.successIcon || "check2-circle",
    "data-wizard-success-document-fields": options.successDocumentFields || "",
    "data-wizard-success-response-fields": options.successResponseFields || "",
    "data-wizard-success-hide-toolbar": options.successHideToolbar === false ? "false" : "true",
    "data-wizard-success-show-document": options.successShowDocument === false ? "false" : "true",
    "data-wizard-success-show-response": options.successShowResponse === false ? "false" : "true",
    "data-wizard-submitting": "false",
    "data-wizard-complete": "false"
  };

  wiz.i = [{
    "n": "div",
    "c": "wizard",
    "i": steps
  }, {
    "n": "div",
    "c": "wizard-footer",
    "i": [{
      "n": "div",
      "c": "d-flex flex-fill p-3",
      "i": {
        "n": "div",
        "data-wizard-id": wizardId,
        "data-wizard-progress": "true",
        "data-wizard-progress-width": ((1 / options.inner.length) * 100) + "%",
        "c": "rounded " + options.progressbarclass + " ua-wizard-progress"
      }
    }, {
      "n": "div",
      "c": "",
      "i": {
        "n": "button",
        "id": wizardId + "-back",
        "c": "m-2 button btn btn-info fw-bold box-shadow text-shadow text-white d-none",
        "i": [{
          "n": "i",
          "c": "bi bi-arrow-left"
        }, {
          "n": "span",
          "c": "d-none d-lg-inline",
          "i": " Back"
        }]
      }
    }, {
      "n": "div",
      "c": "",
      "i": {
        "n": "button",
        "id": wizardId + "-continue",
        "c": "m-2 button btn btn-success fw-bold box-shadow text-shadow text-white",
        "i": continueInner
      }
    }]
  }];

  return wiz;

}; module.exports.render = exports.render;

// ─── After render: wire buttons, prefill, load draft ─────────────────────────

events.on("interface.afterrender", async function() {
  _injectCSS();

  // Set initial progress bar widths
  var bars = document.querySelectorAll("[data-wizard-progress='true'][data-wizard-progress-width]");
  for (var i = 0; i < bars.length; i++) {
    var width = bars[i].getAttribute("data-wizard-progress-width");
    if (width) bars[i].style.width = width;
  }

  // Wire each unwired wizard
  var wizards = document.querySelectorAll("[data-wizard='true']");
  for (var w = 0; w < wizards.length; w++) {
    (function(wizard) {
      if (wizard.getAttribute("data-wizard-wired") === "true") return;
      wizard.setAttribute("data-wizard-wired", "true");

      var wizardId = wizard.getAttribute("id");
      var totalSteps = Number(wizard.getAttribute("data-wizard-steps"));
      var continueBtn = document.getElementById(wizardId + "-continue");
      var backBtn = document.getElementById(wizardId + "-back");
      var inDashboard = _isDashboardContext(wizard);

      // Attach draft listeners (non-PHI, non-dashboard pages only)
      if (!inDashboard && !_isPhiWizard(wizard)) {
        _attachDraftListeners(wizard);
      }

      // Prefill from URL params (non-dashboard pages only)
      if (!inDashboard) {
        _prefillFromUrl(wizardId);
      }

      // Load draft (non-dashboard pages only)
      if (!inDashboard) {
        if (_isPhiWizard(wizard)) {
          _loadServerDraft(wizard).then(function(draftData) {
            if (draftData && window.UATools && typeof window.UATools.load === "function") {
              window.UATools.load(draftData, "#" + wizardId);
            }
          });
        } else {
          var draftData = _loadDraft(wizard);
          if (draftData && window.UATools && typeof window.UATools.load === "function") {
            window.UATools.load(draftData, "#" + wizardId);
          }
        }
      }

      // Wire Continue button
      if (continueBtn) {
        continueBtn.addEventListener("click", async function() {
          if (wizard.getAttribute("data-wizard-complete") === "true") return;
          if (wizard.getAttribute("data-wizard-submitting") === "true") return;

          var currentStep = Number(wizard.getAttribute("data-wizard-current"));
          var totalStepsNow = Number(wizard.getAttribute("data-wizard-steps"));

          // 1. Validate current step
          var validation = await _validateStep(wizard, currentStep);
          if (!validation.success) {
            _shakeStep(wizard, currentStep);
            await _showStepErrors(wizard, currentStep, validation);
            return;
          }
          _clearStepErrors(wizard, currentStep);

          // 2. Run per-step handler (can throw to block advancement)
          var stateData = _collectFields(wizard);
          try {
            await RunHandler(wizardId, currentStep, stateData);
          } catch (handlerErr) {
            _shakeStep(wizard, currentStep);
            var resolvedErr = _resolveError(wizard, handlerErr);
            showWizardResult(wizard, "danger", _buildErrorHtml([resolvedErr]), true);
            return;
          }

          // 3. Save draft on step advance (non-dashboard only)
          if (!_isDashboardContext(wizard)) {
            _saveDraft(wizard, stateData);
          }

          // 4. Advance or submit
          if (currentStep < totalStepsNow) {
            try {
              await load(null, wizard.parentElement, "next");
            } catch (e) {
              console.error("Wizard navigation error", e);
            }
          } else {
            await submitWizard(wizard, continueBtn);
          }
        });
      }

      // Wire Back button
      if (backBtn) {
        backBtn.addEventListener("click", async function() {
          if (wizard.getAttribute("data-wizard-complete") === "true") return;
          if (wizard.getAttribute("data-wizard-submitting") === "true") return;
          try {
            await load(null, wizard.parentElement, "back");
          } catch (e) {
            console.error("Wizard back error", e);
          }
        });
      }
    })(wizards[w]);
  }
});

// ─── Submit ───────────────────────────────────────────────────────────────────

function _extractErrorsFromResponse(wizard, result) {
  var collected = [];

  if (!result) return collected;
  if (result.error) collected.push(result.error);
  if (result.errors instanceof Array) {
    collected = collected.concat(result.errors);
  }
  if (collected.length === 0 && result.success === false) {
    collected.push(result);
  }

  var normalized = [];
  for (var i = 0; i < collected.length; i++) {
    normalized.push(_resolveError(wizard, collected[i]));
  }
  return normalized;
}

function _buildErrorHtml(errors) {
  var list = errors instanceof Array ? errors : [];
  if (list.length === 0) {
    return "<p class='mb-0'>We could not save this step yet. Please review the form and try again.</p>";
  }

  var items = [];
  for (var i = 0; i < list.length; i++) {
    var err = list[i] || {};
    var title = _escapeHtml(err.title || "Error");
    var desc = _escapeHtml(err.description || err.message || "Please review the information and try again.");
    var ns = err.namespace ? ("<div class='small text-muted mt-1'>Namespace: " + _escapeHtml(err.namespace) + "</div>") : "";
    var details = "";
    if (err.errors instanceof Array && err.errors.length > 0) {
      var inner = [];
      for (var j = 0; j < err.errors.length; j++) {
        var issue = err.errors[j] || {};
        var field = issue.field || issue.name || issue.key || issue.path || issue.selector || "";
        var issueText = issue.description || issue.message || issue.title || "One or more fields need attention.";
        var prefix = field ? ("<strong>" + _escapeHtml(field) + ":</strong> ") : "";
        inner.push("<li>" + prefix + _escapeHtml(issueText) + "</li>");
      }
      details = "<ul class='mt-2 mb-0 ps-3'>" + inner.join("") + "</ul>";
    }
    var stack = "";
    if (!err.namespace && err.stacktrace) {
      stack = "<details class='mt-2'><summary>Technical details</summary><pre class='mb-0 small'>" + _escapeHtml(err.stacktrace.toString()) + "</pre></details>";
    }
    items.push("<li><strong>" + title + "</strong><div>" + desc + "</div>" + ns + details + stack + "</li>");
  }

  return "<ul class='mb-0 ps-3'>" + items.join("") + "</ul>";
}

async function submitWizard(wizard, continueBtn) {
  if (wizard.getAttribute("data-wizard-complete") === "true") return;
  if (wizard.getAttribute("data-wizard-submitting") === "true") return;

  var uaeEl = wizard.closest("uae");
  var url = uaeEl ? uaeEl.getAttribute("name") : null;
  if (!url) {
    console.error("Wizard has no name/url attribute.");
    return;
  }

  var wizardId = wizard.getAttribute("id");
  var currentStep = Number(wizard.getAttribute("data-wizard-current"));
  var totalSteps = Number(wizard.getAttribute("data-wizard-steps"));

  // Collect the full document using the same mechanism as save()
  var doc = await _collectDocument(wizard);
  var payload = {
    document: doc.flat,    // flat merged fields — server extracts this for processing
    steps: doc.steps,      // per-step breakdown — developer has full context
    wizard: {
      id: wizardId,
      current: currentStep,
      total: totalSteps
    }
  };

  // Show loading state
  var origHTML = continueBtn.innerHTML;
  wizard.setAttribute("data-wizard-submitting", "true");
  _setWizardNavigationState(wizard, true);
  continueBtn.innerHTML = '<span class="spinner-border spinner-border-sm me-1" role="status"></span>Saving\u2026';

  // Remove any previous result message
  var prev = wizard.querySelector(".ua-wizard-result");
  if (prev) prev.remove();

  try {
    var response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });
    var result = {};
    try {
      result = await response.json();
    } catch (jsonError) {
      result = {};
    }

    if (response.ok && result && result.success) {
      var successPayload = _normalizeSuccessfulPayload(result);
      if (successPayload && successPayload.success === false) {
        showWizardResult(
          wizard,
          "danger",
          _buildErrorHtml([
            _resolveError(wizard, {
              title: "Submit Failed",
              description: successPayload.message || "The request was not accepted."
            })
          ]),
          true
        );
        return;
      }
      _clearDraft(wizard);
      var redirect = (successPayload && successPayload.redirect) || result.redirect || "";
      if (redirect) {
        window.location.href = redirect;
        return;
      }
      var confirmationCfg = _renderSuccessConfirmation(wizard, doc.flat || {}, successPayload);
      _setWizardCompleteState(wizard, true, confirmationCfg.hideToolbar);
    } else {
      var resolvedErrors = _extractErrorsFromResponse(wizard, result || {});
      showWizardResult(wizard, "danger", _buildErrorHtml(resolvedErrors), true);
    }
  } catch (e) {
    console.error("Wizard submit error", e);
    showWizardResult(wizard, "danger", _buildErrorHtml([_resolveError(wizard, e)]), true);
  } finally {
    var completed = wizard.getAttribute("data-wizard-complete") === "true";
    wizard.setAttribute("data-wizard-submitting", "false");
    if (!completed) {
      _setWizardNavigationState(wizard, false);
      continueBtn.innerHTML = origHTML;
    }
  }
}

// ─── Show inline result alert ─────────────────────────────────────────────────

function showWizardResult(wizard, type, message, isHtml) {
  var existing = wizard.querySelector(".ua-wizard-result");
  if (existing) existing.remove();
  var el = document.createElement("div");
  el.className = "alert alert-" + type + " mt-2 ua-wizard-result";
  if (isHtml) {
    el.innerHTML = message;
  } else {
    el.textContent = message;
  }
  var footer = wizard.querySelector(".wizard-footer");
  if (footer) {
    wizard.insertBefore(el, footer);
  } else {
    wizard.appendChild(el);
  }
}

// ─── Save (UAT interface) ─────────────────────────────────────────────────────

/**
 * Returns the full document the wizard is building, plus wizard state.
 * Called by window.UATools.save() when the wizard's uae element is encountered.
 * Always collects data regardless of validation state so drafts can be saved.
 * @param {*} name The name of the control.
 * @param {*} control The control (uae element) to save.
 * @param {*} repo The repo with prevalidation results.
 * @returns repo with data = { document, steps, wizard }
 */
async function save(name, control, repo) {
  var wizard = control.querySelector("[data-wizard='true']");
  var wizardId = wizard.getAttribute("id");
  var currentStep = Number(wizard.getAttribute("data-wizard-current"));
  var totalSteps = Number(wizard.getAttribute("data-wizard-steps"));

  var doc = await _collectDocument(wizard);

  repo.data = {
    document: doc.flat,    // flat merged fields across all steps
    steps: doc.steps,      // per-step breakdown
    wizard: {
      id: wizardId,
      current: currentStep,
      total: totalSteps
    }
  };

  return repo;

} module.exports.save = save;

// ─── Load (UAT interface — step navigation) ───────────────────────────────────

/**
 * Navigate the wizard to a different step.
 * @param {*} name The name of the control.
 * @param {*} control The control's parent element.
 * @param {*} data "next", "back", or a step number.
 */
async function load(name, control, data) {

  var wizard = control.querySelector("[data-wizard='true']");
  if (wizard.getAttribute("data-wizard-complete") === "true") return;
  if (wizard.getAttribute("data-wizard-submitting") === "true") return;
  var wizardId = wizard.getAttribute("id");
  var currentStep = Number(wizard.getAttribute("data-wizard-current"));
  var totalSteps = Number(wizard.getAttribute("data-wizard-steps"));
  var nextStep = currentStep;

  switch (data) {
    case "next":
      if (currentStep < totalSteps) {
        nextStep++;
      } else {
        throw new Error("Cannot go to the next step, reached the end of the wizard.");
      }
      break;
    case "back":
      if (currentStep > 1) {
        nextStep--;
      } else {
        throw new Error("Cannot go back, reached the beginning of the wizard.");
      }
      break;
    default:
      data = parseInt(data);
      if (isNaN(data)) {
        throw new Error("Invalid data type, must be 'next', 'back', or a number.");
      }
      if (data > 0 && data <= totalSteps) {
        nextStep = data;
      } else {
        throw new Error("Invalid step number, must be between 1 and " + totalSteps + ".");
      }
      break;
  }

  // Hide current step
  var currentStepDiv = control.querySelector('[data-wizard-id="' + wizardId + '"][data-wizard-step="' + currentStep + '"]');
  if (currentStepDiv) currentStepDiv.classList.add("d-none");

  // Show next step
  var nextStepDiv = control.querySelector('[data-wizard-id="' + wizardId + '"][data-wizard-step="' + nextStep + '"]');
  if (nextStepDiv) nextStepDiv.classList.remove("d-none");

  // Update progress bar
  var progress = control.querySelector('[data-wizard-id="' + wizardId + '"][data-wizard-progress="true"]');
  if (progress) {
    progress.style.width = (nextStep === totalSteps ? 100 : (nextStep / totalSteps) * 100) + "%";
  }

  // Show/hide back button
  var btnBack = document.getElementById(wizardId + "-back");
  if (btnBack) {
    if (nextStep > 1) {
      btnBack.classList.remove("d-none");
    } else {
      btnBack.classList.add("d-none");
    }
  }

  // Update Continue button label for last vs. intermediate step
  var btnContinue = document.getElementById(wizardId + "-continue");
  if (btnContinue) {
    if (nextStep === totalSteps) {
      btnContinue.innerHTML = '<i class="bi bi-check2"></i><span class="d-none d-lg-inline"> Submit</span>';
    } else {
      btnContinue.innerHTML = '<i class="bi bi-arrow-right"></i><span class="d-none d-lg-inline"> Continue</span>';
    }
  }

  // Track seen steps and reset validation for newly visited steps
  var hasSeen = JSON.parse(wizard.getAttribute("data-wizard-hasSeen") || "[1]");
  if (!hasSeen.includes(nextStep)) {
    ResetValidation('[data-wizard-id="' + wizardId + '"][data-wizard-step="' + nextStep + '"]');
    hasSeen.push(nextStep);
  }
  wizard.setAttribute("data-wizard-hasSeen", JSON.stringify(hasSeen));

  // Update current step
  wizard.setAttribute("data-wizard-current", nextStep);

  // Scroll to top
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;

} module.exports.load = load;

// ─── Validation reset ─────────────────────────────────────────────────────────

async function ResetValidation(selector) {
  var el = document.querySelector(selector);
  if (!el) return;
  el.querySelectorAll(".is-invalid").forEach(function(e) { e.classList.remove("is-invalid"); });
  el.querySelectorAll(".is-valid").forEach(function(e) { e.classList.remove("is-valid"); });
}

// ─── Handlers ─────────────────────────────────────────────────────────────────

/**
 * Add a handler to the wizard.
 * Supports wildcards: set wizardID and/or step to null/false for wildcard matching.
 * @param {*} wizardID The wizard ID to attach the handler to.
 * @param {*} onStep The step to attach the handler to.
 * @param {*} myFunction The function to run. Can throw to block step advancement.
 */
function AddHandler(wizardID, onStep, myFunction) {
  handlerz.push({
    wizard: wizardID,
    step: onStep,
    handler: myFunction
  });
} module.exports.AddHandler = AddHandler;

/**
 * Run the handler(s) for the given wizard and step.
 * @param {*} wizardID The id of the wizard.
 * @param {*} step The step number.
 * @param {*} StateOfTheWizard The current field data collected from the wizard.
 */
async function RunHandler(wizardID, step, StateOfTheWizard) {
  for (var i = 0; i < handlerz.length; i++) {
    var handler = handlerz[i];

    if (!handler.wizard && !handler.step) {
      // wildcard: runs on any wizard, any step
    } else if (!handler.wizard && handler.step == step) {
      // step wildcard: runs on any wizard at this step
    } else if (handler.wizard == wizardID && handler.step == step) {
      // specific wizard and step
    } else {
      continue;
    }

    await handler.handler(StateOfTheWizard);
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

try {
  window.wizard = {
    AddHandler: AddHandler,
    /**
     * Register multiple error overrides into the global registry.
     * @param {Array} arr Array of { namespace, title, description } objects.
     * @param {number} priority 1=dashboard, 2=plugin, 3=app. Higher wins.
     */
    registerErrors: function(arr, priority) {
      if (!(arr instanceof Array)) return;
      for (var i = 0; i < arr.length; i++) {
        var item = arr[i];
        if (!item) continue;
        var ns = item.namespace || item.id || "";
        _registerError(ns, item, typeof priority === "number" ? priority : ERROR_PRIORITY.dashboard);
      }
    },
    /**
     * Register a single error override.
     * @param {string} namespace The error namespace.
     * @param {object} def { title, description, actions, close }.
     * @param {number} priority 1=dashboard, 2=plugin, 3=app.
     */
    registerError: function(namespace, def, priority) {
      _registerError(namespace, def, typeof priority === "number" ? priority : ERROR_PRIORITY.dashboard);
    }
  };
} catch (error) {
  console.error("No window to attach the wizard handler to.");
}
